import { useState } from 'react'
import { WorkspaceService } from 'api/services/WorkspaceService'
import { CanView } from 'components/Can'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { observer } from 'mobx-react-lite'
import { useSelector } from 'react-redux'
import { RootState, UserRole } from 'types'
import Styles from './NavMenu.module.scss'
import { NavMenuHomeItem } from './Buttons/NavMenuHomeItem'
import { NavMenuBrandKitsItem } from 'blaze-components/WorkspaceDashboardSidebar/components/NavMenu/Buttons/NavMenuBrandKitsItem'
import { NavMenuBrandPlanItem } from './Buttons/NavMenuBrandPlanItem'
import { NavMenuTodosItem } from './Buttons/NavMenuTodosItem'
import { NavMenuSharedWithMeItem } from './Buttons/NavMenuSharedWithMeItem'
import { NavMenuContentPreferencesItem } from './Buttons/NavMenuContentPreferencesItem'
import { ExpandableTree, useExpandable } from 'blaze-components/WorkspaceDashboardSidebar/hooks/useExpandable'
import { isToday } from 'utils/dates'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsIntegrationsV3 } from 'hooks/useIsIntegrationsV3'
import { NavMenuTemplateBindersItem } from './Buttons/NavMenuTemplateBindersItem'
import { NavMenuTemplatesPageItem } from './Buttons/NavMenuTemplatesPageItem'
import { NavMenuAnalyticsItem } from './Buttons/NavMenuAnalyticsItem'
import { NavMenuAutopilotIntegrationsItem } from './Buttons/NavMenuAutopilotIntegrationsItem'
import { NavMenuScheduledPostsItem } from './Buttons/NavMenuScheduledPostsItem'
import { NavMenuPaidAdsItem } from './Buttons/NavMenuPaidAdsItem'
import { useIsAdsFeatureEnabled } from 'screens/PaidAds/hooks'
import { NavMenuBrainPlaygroundItem } from './Buttons/NavMenuBrainPlaygroundItem'
import { NavMenuVisualStylesPlaygroundItem } from './Buttons/NavMenuVisualStylesPlaygroundItem'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { NavMenuApprovalsItem } from './Buttons/NavMenuApprovalsItem'

const NavMenu = observer(() => {
  const workspace = useCurrentWorkspace()
  const { toggle, expanded } = useExpandable(ExpandableTree.Configure)
  const { isMobile } = useResponsiveDesign()
  const canSeeTemplateBinders = useFlipperFlag('admin_templates')
  const canSeeIntegrations = useIsIntegrationsV3()
  const canSeePaidAds = useIsAdsFeatureEnabled()
  const canSeeBrainPlayground = useFlipperFlag('brain_playground')
  const canSeeVisualStylesPlayground = useFlipperFlag('visual_styles_playground')
  const canSeeApprovals = useFlipperFlag('approvals_page') && workspace?.post_approval_enabled
  const canSeeContentPreferences = useFlipperFlag('credits_styles_videos')

  const canSeeAnalytics = !workspace?.is_virtual_marketer

  const { sidebarLocked, floatingSidebar } = useSelector(
    ({ sidebar: { locked: sidebarLocked }, floatingSidebar }: RootState) => ({
      floatingSidebar,
      sidebarLocked,
    }),
  )

  const [hasToggledOpen, setHasToggledOpen] = useState(false)

  if (!hasToggledOpen && workspace && isToday(workspace.created_at) && !expanded) {
    toggle()
    setHasToggledOpen(true)
  }

  const closeMobileSidebar = () => {
    if (isMobile && (sidebarLocked || floatingSidebar)) WorkspaceService.hideSidebar()
  }

  if (!workspace) return null

  if (workspace.is_virtual_marketer) {
    return (
      <>
        <ResponsiveDesign.Desktop>
          <ul className={Styles.navItemsList}>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuHomeItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuScheduledPostsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            {canSeePaidAds && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuPaidAdsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeApprovals && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuApprovalsItem workspace={workspace} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuBrandPlanItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuBrandKitsItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            {canSeeContentPreferences && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuContentPreferencesItem workspace={workspace} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeAnalytics && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuAnalyticsItem workspace={workspace} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeIntegrations && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuAutopilotIntegrationsItem onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeBrainPlayground && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuBrainPlaygroundItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            {canSeeVisualStylesPlayground && (
              <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
                <li>
                  <NavMenuVisualStylesPlaygroundItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
                </li>
              </CanView>
            )}
            <CanView as={[UserRole.GUEST]} inWorkspace={workspace.id}>
              <li>
                <NavMenuSharedWithMeItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
          </ul>
        </ResponsiveDesign.Desktop>
        <ResponsiveDesign.Mobile>
          <ul className={Styles.navItemsList}>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuHomeItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuScheduledPostsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
              </li>
            </CanView>

            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuPaidAdsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuBrandPlanItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
            <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
              <li>
                <NavMenuBrandKitsItem workspace={workspace} onPress={closeMobileSidebar} />
              </li>
            </CanView>
          </ul>
        </ResponsiveDesign.Mobile>
      </>
    )
  }

  return (
    <ul className={Styles.navItemsList}>
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuHomeItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuBrandKitsItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuTemplatesPageItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuScheduledPostsItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      {canSeeAnalytics && (
        <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
          <li>
            <NavMenuAnalyticsItem workspace={workspace} onPress={closeMobileSidebar} />
          </li>
        </CanView>
      )}
      <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
        <li>
          <NavMenuTodosItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      {canSeeBrainPlayground && (
        <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
          <li>
            <NavMenuBrainPlaygroundItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
          </li>
        </CanView>
      )}
      {canSeeVisualStylesPlayground && (
        <CanView as={[UserRole.MEMBER, UserRole.ADMIN, UserRole.SUPER_ADMIN]} inWorkspace={workspace.id}>
          <li>
            <NavMenuVisualStylesPlaygroundItem workspaceId={workspace.id} onPress={closeMobileSidebar} />
          </li>
        </CanView>
      )}
      <CanView as={[UserRole.GUEST]} inWorkspace={workspace.id}>
        <li>
          <NavMenuSharedWithMeItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      </CanView>
      {canSeeTemplateBinders && (
        <li>
          <NavMenuTemplateBindersItem workspace={workspace} onPress={closeMobileSidebar} />
        </li>
      )}
    </ul>
  )
})

export { NavMenu }
